<template>
  <section class="monthly-metrics-dashboard">
    <Breadcrumbs
      :breadcrumbs="[
        {title: $t('menu.statistics-2'), disabled: true},
        {title: $t('menu.statistics2/monthly-metrics-dashboard'), disabled: true}
      ]">
      <div slot="breadcrumbAfter" class="d-flex align-items-center">
        <Filters
          :isActive="showFilters"
          :active-filters="activeFilters"
          @toggle-filters="toggleFilters"/>
      </div>
    </Breadcrumbs>

    <div class="row mt-2 no-gutters evollu-form" v-if="showFilters">
      <div class="row">
        <div class="col-12 col-md-5">
          <va-input
            :placeholder="$t('statistics2.monthlyMetrics.filters.month')"
            :label="$t('statistics2.monthlyMetrics.filters.month')"
            v-model="selectedMonthYear"
            id="month-selection"
            :maxlength="255"
            @click="showMonthPicker = !showMonthPicker"
            :readonly="true"
            required
          />
          <div class="month-picker-wrapper" v-if="showMonthPicker">
            <month-picker
              @input="handleMonthSelected"
              :lang="currentLanguage"
              :max-date="moment().toDate()"
              :default-month="filters.dateMonth"
              :default-year="filters.dateYear"
            />
          </div>
        </div>

        <div class="col-12 col-md-4">
          <label class="form-label">
            {{ $t('statistics.csm.entities') }}
          </label>
          <multiselect
            id="entities-filter"
            v-model="filters.entities"
            :options="entityOptions"
            track-by="uid"
            :customLabel="formatedEntityLabel"
            :placeholder="entityOptions.length ? $t('clinical.waiting_room_statistics.several_entities') : $t('clinical.waiting_room_statistics.no_entities')"
            :allow-empty="true"
            :multiple="true"
            :internal-search="false"
            :disabled="!entityOptions.length || isLoading"
            @search-change="asyncFind"
          >

            <template slot="clear">
            <span class="multiselect__clear" v-if="filters.entities.length"
                  @mousedown.prevent.stop="filters.entities = []"></span>
            </template>
          </multiselect>
        </div>
      </div>
    </div>

    <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4" v-if="isLoading"></LoadingAnimation>
    <div class="monthly-metrics-placeholder mt-2" v-if="!isLoading">
      <br>
      <div class="general-kpis row">
        <div class=" row col-12 col-md-12">
          <va-card class="info-widget mr-4 text-center">
            <span class="kpi-content">  {{
                isNaN(dashboardData.firstTimeAppointments) ? "N/A" : dashboardData.firstTimeAppointments
              }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.firstTimeAppointments') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span
              class="kpi-content">  {{ isNaN(dashboardData.diagnostics) ? "N/A" : dashboardData.diagnostics }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.diagnostics') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span class="kpi-content">  {{
                isNaN(dashboardData.noExperiencesOpened) ? "N/A" : dashboardData.noExperiencesOpened
              }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.noExperiencesOpened') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span
              class="kpi-content">  {{
                isNaN(dashboardData.experiencesOpened) ? "N/A" : dashboardData.experiencesOpened
              }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.experiencesOpened') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span
              class="kpi-content">  {{
                isNaN(dashboardData.experiencesActive) ? "N/A" : dashboardData.experiencesActive
              }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.experiencesActive') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span class="kpi-content">  {{ isNaN(dashboardData.returns) ? "N/A" : dashboardData.returns }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.returns') }} </span>
            </p>
          </va-card>

          <va-card class="info-widget mr-4 text-center">
            <span class="kpi-content">  {{ isNaN(dashboardData.sales) ? "N/A" : dashboardData.sales }} </span>
            <p>
              <span class="kpi-description">  {{ $t('statistics2.monthlyMetrics.sales') }} </span>
            </p>
          </va-card>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Breadcrumbs from "@/components/common/Breadcrumbs";
import Filters from '@/components/common/Filters'
import {MonthPicker, MonthPickerInput} from 'vue-month-picker'
import {Statistics2Service} from "@/api/StatisticsService";
import moment from "moment";
import LoadingAnimation from "@/components/users/components/Helpers/LoadingAnimation";
import MultiselectLabelFormatter from "@/mixins/MultiselectLabelFormatter";
import ClustersAndEntitiesMixin from "@/mixins/ClustersAndEntitiesMixin";
import {mapGetters} from "vuex";
import Multiselect from "vue-multiselect";
import LocalStorageHelper from "@/local-storage/LocalStorageHelper";
import {STATISTICS2_MONTHLY_DASHBOARD} from "@/local-storage/LocalStorageKeys";

export default {
  name: "index.vue",
  mixins: [MultiselectLabelFormatter, ClustersAndEntitiesMixin],
  components: {LoadingAnimation, Filters, Breadcrumbs, MonthPicker, MonthPickerInput, Multiselect},
  data() {
    return {
      showFilters: true,
      activeFilters: 0,
      isLoading: false,
      dashboardData: {},
      entitiesAllowed: [],
      clustersAllowed: [],
      selectedEntityIds: [],
      selectedEntityUids: [],
      entityOptions: [],
      hasLoadedLocalStorage: false,
      filters: {
        dateMonth: moment().month() + 1,
        dateYear: moment().year(),
        entities: []
      },
      selectedMonthYear: moment().format('YYYY-MM'),
      showMonthPicker: false
    }
  },
  async created() {
    this.isLoading = true
    this.entitiesAllowed = this.authorizedEntities
    this.entitiesAllowed = this.entitiesAllowed.filter(ent => ent.business_plan_id === 5 || ent.business_plan_id === 7)
    let entitiesAllowedIds = this.entitiesAllowed.map(ent => ent.id)
    this.clustersAllowed = this.clusters
    this.clustersAllowed = this.clustersAllowed.filter(cluster => cluster.entitiesIds.some(id => entitiesAllowedIds.includes(id)))
    this.generateEntityOptions()

    await this.getStatistics().finally(() => {
      this.isLoading = false
    })


  },
  methods: {
    handleMonthSelected(date) {
      this.filters.dateMonth = date.monthIndex
      this.filters.dateYear = date.year
      this.showMonthPicker = !this.showMonthPicker
    },

    getStatistics: function () {
      return new Promise(async (resolve, reject) => {
        try {
          const params = this.getActiveFilters()
          this.dashboardData = await Statistics2Service.getMonthlyDashboardData(params)
          await this.saveLocalStorage()
          resolve()
        } catch (e) {
          console.log(e)
          this.showToast(this.$t('clinical.waiting_room_statistics.something_went_wrong'), this.$store.state.errorToastOptions)
          reject(e)
        }
      })
    },
    getActiveFilters: function () {
      let entities = null
      if (this.filters.entities.length) {
        if (this.filters.entities[0].entities !== undefined) {
          entities = this.filters.entities[0].entities.map(entity => entity.id).join(',')
        } else {
          entities = this.filters.entities.map(entity => entity.id).join(',')
        }
      }

      let filters = {
        start: this.moment(`${this.filters.dateYear}-${this.filters.dateMonth}-1`, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        end: this.moment(`${this.filters.dateYear}-${this.filters.dateMonth}-1`, 'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD'),
        selectedEntityUids: Array.from(this.selectedEntityUids) || null
      }
      if (entities) {
        filters.entities = entities
      }
      return filters
    },
    loadLocalStorage: async function () {
      const state = LocalStorageHelper.get(STATISTICS2_MONTHLY_DASHBOARD)
      if (state) {
        // s date filters from local storage
        if (state.start && state.start !== '' && state.end && state.end !== '') {
          this.filters.dateMonth = this.moment(state.start).month() + 1
          this.filters.dateYear = this.moment(state.start).year()
        }

        if (Array.isArray(state.selectedEntityUids) && state.selectedEntityUids && state.selectedEntityUids.length > 0) {
          this.filters.entities = this.reloadSelectedClustersAndEntities(this.entitiesAllowed, state.selectedEntityUids)
        }
      }
    },
    saveLocalStorage: function () {
      LocalStorageHelper.set(STATISTICS2_MONTHLY_DASHBOARD, {
        ...this.getActiveFilters(),
      })
      this.hasLoadedLocalStorage = true
    },
    toggleFilters: function () {
      this.showFilters = !this.showFilters
    },
    asyncFind(search_query) {
      this.generateEntityOptions(search_query)
    },
    generateEntityOptions(search_query) {
      if (search_query) {
        this.entityOptions = this.createClustersAndEntitiesListSearch(this.clustersAllowed, this.entitiesAllowed, search_query)
      } else {
        this.entityOptions = this.createClustersAndEntitiesList(this.clustersAllowed, this.entitiesAllowed)
      }

    },
  },
  computed: {
    ...mapGetters({
      clusters: 'clusters',
    }),
    authorizedEntities: function () {
      let statsV2Service = this.$store.state.services.find(service => service.url === this.$store.state.statistics2_server) || null
      if (statsV2Service) {
        if (statsV2Service.user_permissions.some(p => p.entity_id === null)) {
          return statsV2Service.entities
        } else {
          return statsV2Service.entities.filter(entity => statsV2Service.user_permissions.filter(p => p.entity_id === entity.id))
        }
      }
      return null
    },
    currentLanguage: function () {
      if(this.$i18n.locale() === 'gb') {
        return 'en'
      }
      return this.$i18n.locale()
    }
  },
  watch: {
    'filters.entities': function (val) {
      if (this.hasLoadedLocalStorage) {
        this.getStatistics()
          .then(() => {
            this.isLoading = false
          })
      }
    },
    'filters.dateMonth': {
      handler: function () {
        this.isLoading = true
        this.selectedMonthYear = moment(`${this.filters.dateYear}-${this.filters.dateMonth}-1`, 'YYYY-MM-DD').format('YYYY-MM')
        this.getStatistics().finally(() => {this.isLoading = false})
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.general-kpis {
  margin-left: 20px !important;
}

.kpi-content {
  font-size: 1.5rem;
  font-weight: bold;
  vertical-align: text-bottom;
}

.info-widget {
  width: 160px
}

.month-picker__container{
  z-index: 10;
  position: absolute;
  min-width: 150px;
}

.month-picker__month.selected, .month-picker__month.selected-range-first, .month-picker__month.selected-range-second {
  background-color: #29c4a9;
  box-shadow: inset 0 0 3px #29c4a9;
}
</style>
